import React, { useEffect, useState } from "react";
import { EditorField, useEditorStore } from "../../../../store";
import { LocationService } from "../../../../services/location.service";
import { SelectField, getFieldIcon } from "@codway-dev/ui";

interface LocationSelectPreviewProps {
  field: EditorField;
  dynamicStyle: React.CSSProperties;
  onUpdate: (value: string) => void;
  error?: string;
  type: "province" | "city";
}

export const LocationSelectPreview: React.FC<LocationSelectPreviewProps> = ({
  field,
  dynamicStyle,
  onUpdate,
  error,
  type,
}) => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    []
  );
  const [loading, setLoading] = useState(false);

  const provinceValue = useEditorStore(
    (state) =>
      state.config.fields.find((f) => f.key === "zone_code")?.defaultValue
  );

  useEffect(() => {
    let isMounted = true;

    const fetchOptions = async () => {
      setLoading(true);
      try {
        if (type === "province") {
          const departments = await LocationService.getDepartments();
          if (isMounted) {
            setOptions(
              departments.map((d) => ({
                label: d.name,
                value: d.name, // We store the Name, not ID
              }))
            );
          }
        } else if (type === "city") {
          const provinceName = provinceValue;

          if (!provinceName) {
            if (isMounted) setOptions([]);
            return;
          }

          const departments = await LocationService.getDepartments();
          const selectedDept = departments.find((d) => d.name === provinceName);

          if (selectedDept) {
            const cities = await LocationService.getCities(selectedDept.id);
            if (isMounted) {
              setOptions(
                cities.map((c) => ({
                  label: c.name,
                  value: c.name,
                }))
              );
            }
          }
        }
      } catch (err) {
        console.error("Failed to load location data", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchOptions();

    return () => {
      isMounted = false;
    };
  }, [type, provinceValue]);

  const isDisabled = loading || (type === "city" && options.length === 0);

  let placeholderText = field.placeholder || "Seleccionar...";
  if (loading) {
    placeholderText = "Cargando...";
  } else if (type === "city" && options.length === 0) {
    placeholderText = "Selecciona un departamento primero";
  }

  const icon = field.show_icon
    ? getFieldIcon(field.key, field.type)
    : undefined;

  return (
    <div style={dynamicStyle as React.CSSProperties}>
      <SelectField
        label={field.label}
        value={field.defaultValue || ""}
        options={options}
        onChange={onUpdate}
        placeholder={placeholderText}
        required={field.required}
        disabled={isDisabled}
        error={error}
        icon={icon}
      />
    </div>
  );
};
